import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Plus, Search, Clock, Package, Bookmark, FileText, LineChart, ArrowRight } from 'lucide-react';

const Dashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedCount, setSavedCount] = useState(null);
  const [templatesCount, setTemplatesCount] = useState(null);
  const [pipelineCount, setPipelineCount] = useState(null);
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const fetchRequests = React.useCallback(async () => {
    try {
      const { data } = await api.get('/api/requests');
      setRequests(data);
    } catch (error) {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Lightweight fetches for the mobile workspace shortcut counts (non-blocking)
  const fetchWorkspaceCounts = React.useCallback(async () => {
    try {
      const [savedRes, tmplRes] = await Promise.all([
        api.get('/api/saved-suppliers').catch(() => ({ data: [] })),
        api.get('/api/templates').catch(() => ({ data: [] })),
      ]);
      const saved = savedRes.data || [];
      setSavedCount(saved.length);
      setTemplatesCount((tmplRes.data || []).length);
      // "Active" outreach = anything past 'not_contacted' and before 'closed'
      const active = saved.filter(
        (s) => s.status && s.status !== 'not_contacted' && s.status !== 'closed'
      ).length;
      setPipelineCount(active);
    } catch (e) {
      // Silently ignore; cards will simply not show counts
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchWorkspaceCounts();
  }, [fetchRequests, fetchWorkspaceCounts]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getBestSupplier = (suppliers) => {
    if (!suppliers || suppliers.length === 0) return null;
    return suppliers[0];
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Title + New Search. Mobile: stack vertically, full-width CTA. Desktop: inline. */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">{t('dashboard')}</h1>
            <p className="mt-1.5 text-sm md:text-base text-slate-600">{t('manageSearches')}</p>
          </div>
          <Button
            onClick={() => navigate('/new-request')}
            className="bg-blue-600 hover:bg-blue-700 transition-all duration-200 h-11 md:h-10 w-full md:w-auto"
            data-testid="new-request-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('newSearch')}
          </Button>
        </div>

        {/* Mobile workspace shortcuts — makes Saved / Templates / Tracker discoverable */}
        <section className="md:hidden mb-7" data-testid="mobile-workspace-shortcuts">
          <h2 className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-2.5 font-medium">{t('workspaceTitle')}</h2>
          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => navigate('/saved')}
              className="bg-white border border-slate-200 rounded-xl p-4 text-left active:bg-slate-50 hover:border-slate-300 transition-colors flex flex-col min-h-[110px]"
              data-testid="workspace-card-saved"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Bookmark className="w-5 h-5 text-blue-600" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-2.5">
                <div className="text-sm font-semibold text-slate-900">{t('shortcutSavedTitle')}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {savedCount !== null ? t('shortcutSavedCount').replace('{n}', savedCount) : t('shortcutSavedSub')}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/saved?view=tracker')}
              className="bg-white border border-slate-200 rounded-xl p-4 text-left active:bg-slate-50 hover:border-slate-300 transition-colors flex flex-col min-h-[110px]"
              data-testid="workspace-card-tracker"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <LineChart className="w-5 h-5 text-emerald-600" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-2.5">
                <div className="text-sm font-semibold text-slate-900">{t('shortcutTrackerTitle')}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {pipelineCount !== null ? t('shortcutTrackerCount').replace('{n}', pipelineCount) : t('shortcutTrackerSub')}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/templates')}
              className="bg-white border border-slate-200 rounded-xl p-4 text-left active:bg-slate-50 hover:border-slate-300 transition-colors flex flex-col min-h-[110px]"
              data-testid="workspace-card-templates"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-violet-600" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-2.5">
                <div className="text-sm font-semibold text-slate-900">{t('shortcutTemplatesTitle')}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {templatesCount !== null ? t('shortcutTemplatesCount').replace('{n}', templatesCount) : t('shortcutTemplatesSub')}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate('/new-request')}
              className="bg-white border border-slate-200 rounded-xl p-4 text-left active:bg-slate-50 hover:border-slate-300 transition-colors flex flex-col min-h-[110px]"
              data-testid="workspace-card-new-search"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Search className="w-5 h-5 text-amber-600" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-2.5">
                <div className="text-sm font-semibold text-slate-900">{t('shortcutSearchTitle')}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t('shortcutSearchSub')}</div>
              </div>
            </button>
          </div>
        </section>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        )}

        {!loading && requests.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="empty-state">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{t('noSearchesYet')}</h3>
            <p className="text-slate-600 mb-6">{t('noSearchesDesc')}</p>
            <Button
              onClick={() => navigate('/new-request')}
              className="bg-blue-600 hover:bg-blue-700 transition-all duration-200"
              data-testid="empty-state-new-request-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('createNewSearch')}
            </Button>
          </div>
        )}

        {!loading && requests.length > 0 && (
          <div className="grid grid-cols-1 gap-4 stagger-fade-in" data-testid="requests-list">
            {requests.map((request) => {
              const bestSupplier = getBestSupplier(request.suppliers);
              return (
                <div
                  key={request.id}
                  className="bg-white rounded-lg border border-slate-200 p-6 hover:border-blue-300 transition-all duration-200 cursor-pointer"
                  onClick={() => navigate(`/results/${request.id}`)}
                  data-testid="request-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{request.product_name}</h3>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            {request.updated_at && request.updated_at !== request.created_at
                              ? `${t('lastUpdated')}: ${formatDate(request.updated_at)} ${formatTime(request.updated_at)}`
                              : formatDate(request.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">{t('quantity')}</span>
                          <p className="font-medium text-slate-900">{request.quantity} {t('units')}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">{t('budget')}</span>
                          <p className="font-medium text-slate-900">${request.budget.toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">{t('suppliersFound')}</span>
                          <p className="font-medium text-slate-900">{request.suppliers?.length || 0}</p>
                        </div>
                        {bestSupplier && (
                          <div>
                            <span className="text-slate-500">{t('bestMatch')}</span>
                            <p className="font-medium text-slate-900">{bestSupplier.name}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
